import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { navigate } from 'gatsby';
import InputGroup from 'react-bootstrap/InputGroup' ;
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { useForm } from 'react-hook-form';
import { useLocation } from '@gatsbyjs/reach-router';
import { isBrowser } from '@helpers/utils';

import Container from '@common/Container';
import Heading from '@common/Heading';
import Section from '@common/Section';
import Row from '@common/Row';
import Button from '@common/Button';
import RichText from '@common/RichText';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import Hearts from '@images/hearts.svg';

const PAGE_SIZE = 15;

import * as styles from './users-affirmations.module.scss';

const UsersAffirmations = ({ data }) => {
  const {
    title,
    showTitle,
    list,
    bgColour
  } = data;
  const { register, handleSubmit, setValue } = useForm();
  const { search } = useLocation();
  const [filteredList, setFilteredList] = useState(list);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const params = new URLSearchParams(search);
  const query = params.get('q');
  const filterList = (filter) => {
    let newList = list;
    if (!filter || filter === '') {
      setFilteredList(list);
    } else {
      newList = newList.filter(x => x.name?.toLowerCase().includes(filter.toLowerCase()) || x.company?.toLowerCase().includes(filter.toLowerCase()));
      setFilteredList(newList.sort());
    }
  }
  const scrollToTop = () => isBrowser() && window.scrollTo(0,0);
  const nextPage = () => setCurrentPage(currentPage+1);
  const prevPage = () => setCurrentPage(currentPage-1);
  const goToPage = (page) => setCurrentPage(page);
  const onSubmit = data => {
    setIsLoading(true);
    filterList(data.query);
    goToPage(1);
    if (data.query === '' || data.query == null) {
      params.delete('q')
    } else {
      params.set('q', data.query);
    }
    setTimeout(() => setIsLoading(false), 300);
    navigate(`/resources/affirmations?${params.toString()}`);
    scrollToTop();
  };
  const setPageParam = () => {
    if (filteredList.length <= PAGE_SIZE) {
      params.delete('page')
    } else {
      params.set('page', currentPage);
    }
    navigate(`/resources/affirmations?${params.toString()}`);
    scrollToTop();
  }
  useEffect(() => {
    // Set value from query on page load
    setValue('query', query);
    filterList(query);
  }, [query]);
  useEffect(() => {
    setPageParam(currentPage);
  }, [filteredList, currentPage]);
  return (
    <Section className={clsx({[`bg-${bgColour} py-5`]: bgColour })}>
      <Container>
        {(showTitle && title) && <Heading type='h2' className='mx-auto mb-4 col-lg-10 text-center'>{title}</Heading>}
        <Form onSubmit={handleSubmit(onSubmit)} className='bg-teal rounded p-3 mb-5'>
          <Row>
            <div className='col'>
              <Form.Label className='text-uppercase fs-12'>{'Search by educator or center name'}</Form.Label>
              <InputGroup>
                <InputGroup.Text id='searchBar' className='bg-white p-1 border-white border-1'>
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    size={'xl'}
                  />
                </InputGroup.Text>
                <Form.Control className='shadow-none border-white border-1' placeholder='Search' {...register('query')}/>
              </InputGroup>
            </div>
            <div className='d-flex align-items-end col-4'>
              <Button
                type='submit'
                variant='black'
                copy='Search'
                addClass='w-100'
              />
            </div>
          </Row>
        </Form>
        {!isLoading && (filteredList.length > 0) &&
          <Row className={clsx('gy-3 mb-5', styles.listAffirmation)}>
            {filteredList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((user, index) => (
              <div key={index}>
                <div className={clsx('d-flex justify-content-center position-relative bg-affirmation rounded py-5', styles.bgAffirmation)}>
                  <div className='col-8'>
                    {user.name &&
                      <p className='fs-32 mb-1'>{user.name}</p>
                    }
                    {user.review &&
                      <RichText json={user.review}/>
                    }
                  </div>
                  <img src={Hearts} alt='' style={{
                    position: 'absolute',
                    top: '0',
                    left: '0'
                  }}/>
                  <img src={Hearts} alt='' style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0'
                  }}/>
                </div>
                
              </div>
            ))}
          </Row>
        }
        {!isLoading && (filteredList == null || filteredList.length < 1) &&
          <Row>
            <p>{'No Results found. Try changing the search field and try again.'}</p>
          </Row>
        }
        {!isLoading && (filteredList.length > PAGE_SIZE) &&
          <Row className='justify-content-between align-items-center'>
            <div className='col-auto'>
              <Button onClick={prevPage} disabled={currentPage === 1} addClass='border-0'>
                <FontAwesomeIcon icon={faChevronLeft} size='lg'/>
                <span className='sr-only sr-only-focusable'>
                  {'Prev'}
                </span>
              </Button>
            </div>
            {new Array(Math.ceil(filteredList.length/PAGE_SIZE)).fill(null).map((page, index) => (
              <div key={index} className='col-auto d-none d-lg-block'>
                <button onClick={() => goToPage(index+1)} disabled={currentPage === index+1} className={clsx(styles.btnPagination, {[styles.active]:currentPage === index+1})}>
                  {index+1}
                </button>
              </div>
            ))}
            <div className='col-auto'>
              <Button onClick={nextPage} disabled={currentPage === Math.ceil(filteredList.length/PAGE_SIZE)} addClass='border-0'>
                <FontAwesomeIcon icon={faChevronRight} size='lg'/>
                <span className='sr-only sr-only-focusable'>
                  {'Next'}
                </span>
              </Button>
            </div>
          </Row>
        }
        <Spinner variant='dark-teal' animation='border' role='status' className={clsx({'d-none':!isLoading})}>
          <span className='sr-only sr-only-focusable'>{'Loading...'}</span>
        </Spinner>
      </Container>
    </Section>
  )
}

export default UsersAffirmations
